import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

interface CaregiverPortalState {
  /** null while loading or after a failed load; false for non-caregivers or an unfinished form. */
  formSubmitted: boolean | null;
  /** The last load failed (network/server error); callers should offer a retry rather than redirect. */
  loadFailed: boolean;
  firstName: string | null;
  refresh: () => Promise<void>;
}

const CaregiverPortalContext = createContext<CaregiverPortalState | undefined>(undefined);

interface LoadedState {
  userId: number;
  formSubmitted: boolean | null;
  loadFailed: boolean;
  firstName: string | null;
}

// The caregiver must re-verify their phone; /caregiverform already handles these, so they count as "not submitted".
const RELINK_CODES = new Set(['CAREGIVER_REGISTRATION_REQUIRED', 'CAREGIVER_LINK_CONFLICT']);

/** Whether the signed-in caregiver has submitted the portal form; drives the hub, nav and availability access. */
export function CaregiverPortalProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, hasRole, authFetch } = useAuth();
  const [loaded, setLoaded] = useState<LoadedState | null>(null);
  const userId = user?.id ?? null;
  const isCaregiver = isAuthenticated && hasRole('caregiver');

  // Only the newest request for the current user may write state: a slower response from a
  // replaced session (e.g. a PIN link signing someone else in) must not land afterwards.
  const latestRequestRef = useRef(0);
  const currentUserIdRef = useRef<number | null>(userId);
  currentUserIdRef.current = userId;

  const refresh = useCallback(async () => {
    if (userId === null) return;
    const requestId = ++latestRequestRef.current;
    const commit = (state: Omit<LoadedState, 'userId'>) => {
      if (requestId === latestRequestRef.current && currentUserIdRef.current === userId) {
        setLoaded({ userId, ...state });
      }
    };

    if (!isCaregiver) {
      commit({ formSubmitted: false, loadFailed: false, firstName: null });
      return;
    }
    try {
      const response = await authFetch('/api/caregiver/portal-state');
      if (response.ok) {
        const data = await response.json();
        commit({ formSubmitted: data?.formSubmitted === true, loadFailed: false, firstName: data?.firstName ?? null });
        return;
      }
      const body = response.status === 409 ? await response.json().catch(() => null) : null;
      if (RELINK_CODES.has(body?.code)) {
        commit({ formSubmitted: false, loadFailed: false, firstName: null });
        return;
      }
      commit({ formSubmitted: null, loadFailed: true, firstName: null });
    } catch {
      commit({ formSubmitted: null, loadFailed: true, firstName: null });
    }
  }, [userId, isCaregiver, authFetch]);

  useEffect(() => {
    refresh();
  }, [userId, isCaregiver]);

  // A result for a previous user (or none yet) counts as loading, so guards never act on stale state.
  const current = loaded && loaded.userId === userId ? loaded : null;

  return (
    <CaregiverPortalContext.Provider
      value={{
        formSubmitted: current ? current.formSubmitted : null,
        loadFailed: current?.loadFailed ?? false,
        firstName: current?.firstName ?? null,
        refresh,
      }}
    >
      {children}
    </CaregiverPortalContext.Provider>
  );
}

export function useCaregiverPortal(): CaregiverPortalState {
  const context = useContext(CaregiverPortalContext);
  if (context === undefined) {
    throw new Error('useCaregiverPortal must be used within a CaregiverPortalProvider');
  }
  return context;
}
